import { useEffect, useState } from "react";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import { formatCurrency, formatInteger } from "../../../../../formatters";
import { BrandMtdRow } from "../../../../../Brands/brandsApiMapping";
import MobileTablePagination from "../../../../MobileTablePagination";

// Every desktop column carried over (Story 4 - "display every column
// available on desktop"), matching BRANDS_MTD_CSV_HEADERS' field set exactly,
// not the mockup's trimmed 7-column illustration (the mockup's visual layout
// is reference-only - see docs/module-configs/optifund-dashboard-mobile.jsonc
// caveat 1).
const COLUMNS: Array<{ key: keyof BrandMtdRow; label: string; format: (row: BrandMtdRow) => string }> = [
  { key: "qualified", label: "Qualified", format: (r) => formatInteger(r.qualified) },
  { key: "reported", label: "Reported", format: (r) => formatInteger(r.reported) },
  { key: "paid", label: "Paid", format: (r) => formatInteger(r.paid) },
  { key: "income", label: "Total income", format: (r) => formatCurrency(r.income) },
  { key: "epq", label: "EPQ", format: (r) => formatCurrency(r.epq) },
  { key: "epp", label: "EPP", format: (r) => formatCurrency(r.epp) },
  { key: "failure", label: "Failure", format: (r) => formatInteger(r.failure) },
  { key: "pending", label: "Pending", format: (r) => formatInteger(r.pending) },
  { key: "initial", label: "Initial", format: (r) => formatInteger(r.initial) },
];

const BRAND_COL_WIDTH = 150;
const DATA_COL_WIDTH = 92;
const GRID_TEMPLATE = `${BRAND_COL_WIDTH}px repeat(${COLUMNS.length}, ${DATA_COL_WIDTH}px)`;
const DEFAULT_PAGE_SIZE = 10;

interface BrandsMtdTableProps {
  rows: BrandMtdRow[];
}

// Mobile's replacement for the desktop BrandsMtdTable.tsx: Brand is the
// literal first column here, so it's the one that's sticky (per the mockup
// and Story 4's "keep the first column fixed"). Paginated (rows-per-page
// picker + prev/next, defaulting to 10/page like desktop) instead of
// rendering every row at once.
export default function BrandsMtdTable({ rows }: BrandsMtdTableProps) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    setPage(0);
  }, [rows]);

  const handlePageSizeChange = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPage(0);
  };

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pagedRows = rows.slice(safePage * pageSize, safePage * pageSize + pageSize);

  return (
    <Paper variant="outlined" sx={{ p: "16px 14px", borderColor: "#ececec" }}>
      <Typography sx={{ fontSize: 15, fontWeight: 500, color: "#1a1a1a", mb: 1.5 }}>Brands Performance - MTD</Typography>

      <Box sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch", border: "1px solid #ececec", borderRadius: "10px" }}>
        <Box sx={{ minWidth: BRAND_COL_WIDTH + COLUMNS.length * DATA_COL_WIDTH }}>
          <Box sx={{ display: "grid", gridTemplateColumns: GRID_TEMPLATE, borderBottom: "1px solid #ececec" }}>
            <Box
              sx={{
                position: "sticky",
                left: 0,
                zIndex: 2,
                p: "11px 12px",
                fontSize: 11,
                fontWeight: 700,
                color: "#8E24AA",
                backgroundColor: "#faf8fb",
                borderRight: "1px solid #f0e8f3",
              }}
            >
              Brand
            </Box>
            {COLUMNS.map((column) => (
              <Box
                key={column.key}
                sx={{ p: "11px 8px", fontSize: 11, fontWeight: 700, color: "#8E24AA", textAlign: "right", backgroundColor: "#faf8fb" }}
              >
                {column.label}
              </Box>
            ))}
          </Box>

          {rows.length === 0
            ? Array.from({ length: 2 }).map((_, index) => (
                <Box key={index} sx={{ display: "grid", gridTemplateColumns: GRID_TEMPLATE, borderBottom: "1px solid #f4f4f4" }}>
                  <Box sx={{ position: "sticky", left: 0, backgroundColor: "#fff", p: "11px 12px" }}>
                    <Skeleton variant="text" width="70%" />
                  </Box>
                  {COLUMNS.map((column) => (
                    <Box key={column.key} sx={{ p: "11px 8px" }}>
                      <Skeleton variant="text" />
                    </Box>
                  ))}
                </Box>
              ))
            : pagedRows.map((row) => (
                <Box key={row.brand} sx={{ display: "grid", gridTemplateColumns: GRID_TEMPLATE, borderBottom: "1px solid #f4f4f4" }}>
                  <Box
                    sx={{
                      position: "sticky",
                      left: 0,
                      zIndex: 1,
                      backgroundColor: "#fff",
                      borderRight: "1px solid #f4f4f4",
                      p: "11px 12px",
                      fontSize: 12.5,
                      fontWeight: 500,
                      color: "#212121",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.brand}
                  </Box>
                  {COLUMNS.map((column) => (
                    <Box key={column.key} sx={{ p: "11px 8px", fontSize: 12.5, textAlign: "right", color: "#616161" }}>
                      {column.format(row)}
                    </Box>
                  ))}
                </Box>
              ))}
        </Box>
      </Box>

      {rows.length > 0 && (
        <MobileTablePagination
          total={rows.length}
          pageSize={pageSize}
          page={safePage}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </Paper>
  );
}
